import React, { useEffect, useState } from 'react'

import {
    CepApprovedIcon,
    Label,
    Spinner,
    Button,
    VsStar2Icon,
} from '@octane/spark'
import { ApprovalTerm } from 'api/application'
import ClientApplication from 'api/application/interfaces/ClientApplication'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useApprovalTerms from 'hooks/useApprovalTerms'
import AssetType from 'state/enums/AssetType'
import VehicleType from 'state/enums/VehicleType'
import { formatCurrency } from 'utils/formatCurrency'

import {
    Container,
    ApprovedTitle,
    BodyText,
    AprText,
    LoaderWrapper,
    LabelWrapper,
} from './DecisionResult.styled'

const PRIME_RISK_TIERS = ['1', '2']

const hasPrimeRiskTier = (maxFinancedTerm: ApprovalTerm) => {
    return PRIME_RISK_TIERS.includes(maxFinancedTerm.risk_tier)
}

const getMaxFinancedAmount = (approvalTerm: ApprovalTerm) => {
    return (
        Number(approvalTerm?.computed_max_finance) +
        Number(approvalTerm?.additional_oem_accessories_dollar_amount)
    )
}

const getMaxFinancedTerm = (approvalTerms: ApprovalTerm[]) => {
    return approvalTerms.reduce(
        (maxFinancedTerm, approvalTerm: ApprovalTerm) => {
            if (!maxFinancedTerm) {
                return approvalTerm
            }

            const currentApprovalTermTotal = getMaxFinancedAmount(approvalTerm)
            const maxTermAmount = getMaxFinancedAmount(maxFinancedTerm)

            if (currentApprovalTermTotal > maxTermAmount) {
                return approvalTerm
            }

            return maxFinancedTerm
        },
        null,
    )
}

const getMinAPR = (approvalTerms: ApprovalTerm[]) => {
    return approvalTerms.reduce((min, approvalTerm: ApprovalTerm) => {
        const apr = Number(approvalTerm.apr)

        return apr < min ? apr : min
    }, 1000)
}

export default function DecisionResultApproved({
    clientApplication,
    vehicleType,
    vehicleCondition,
}: {
    readonly clientApplication: ClientApplication
    readonly vehicleType: string
    readonly vehicleCondition: string
}): JSX.Element {
    const [maxFinancedAmount, setMaxFinancedAmount] = useState<number>()
    const [hasGuaranteedBackend, setHasGuaranteedBackend] = useState(false)
    const [isPrime, setIsPrime] = useState(false)
    const { approvalTerms, loading } = useApprovalTerms(clientApplication?.uuid)
    const maxFinancedTerm = getMaxFinancedTerm(approvalTerms)
    const minAPR = getMinAPR(approvalTerms)
    const featureFlags = useFeatureFlagContext()

    const isPowersportsApplication =
        clientApplication.asset_type === AssetType.POWERSPORTS_VEHICLE

    const isTrailer = vehicleType === VehicleType.TRAILER
    const hasTrailerAdvance = isPowersportsApplication && !isTrailer

    const hasAllInAdvance =
        featureFlags?.singleBucket &&
        isPrime &&
        clientApplication.asset_type === AssetType.POWERSPORTS_VEHICLE &&
        vehicleType !== VehicleType.PWC &&
        vehicleCondition === 'N'

    const noIncomeVerificationRequired =
        clientApplication.requires_income_verification === false

    const hasOemAccessoriesAdvance =
        Number(maxFinancedTerm?.additional_oem_accessories_dollar_amount) > 0

    useEffect(() => {
        if (maxFinancedTerm) {
            setMaxFinancedAmount(getMaxFinancedAmount(maxFinancedTerm))

            setHasGuaranteedBackend(
                Number(maxFinancedTerm.guaranteed_backend_amount) > 0,
            )

            setIsPrime(hasPrimeRiskTier(maxFinancedTerm))
        }
    }, [maxFinancedTerm])

    const renderLabels = () => {
        if (
            !isPrime &&
            !hasGuaranteedBackend &&
            !noIncomeVerificationRequired &&
            !hasAllInAdvance
        ) {
            return null
        }

        return (
            <LabelWrapper>
                {isPrime && (
                    <Label
                        text="Prime"
                        color="success"
                        startIcon={VsStar2Icon}
                    />
                )}
                {noIncomeVerificationRequired && (
                    <Label
                        text="No Income Verification Required"
                        color="success"
                    />
                )}
                {hasGuaranteedBackend && !hasAllInAdvance && (
                    <Label text="Guaranteed Backend" color="success" />
                )}
                {hasAllInAdvance && (
                    <Label text="All-in-Advance" color="success" />
                )}
            </LabelWrapper>
        )
    }

    const getAdvanceText = () => {
        if (!hasAllInAdvance) {
            if (hasOemAccessoriesAdvance && hasTrailerAdvance) {
                return 'with Accessories Advance + Trailer Advance'
            }

            if (!hasTrailerAdvance && hasOemAccessoriesAdvance) {
                return 'with Accessories Advance'
            }
        }

        if (hasTrailerAdvance) {
            return 'with Trailer Advance'
        }

        return ''
    }

    return (
        <Container data-testid="DecisionResultApproved">
            <CepApprovedIcon />
            <ApprovedTitle>Approved</ApprovedTitle>

            {loading || !maxFinancedAmount ? (
                <LoaderWrapper
                    data-testid="DecisionResultApproved-spinner"
                    style={{ height: '120px' }}
                >
                    <Spinner size={24} />
                </LoaderWrapper>
            ) : (
                <>
                    {renderLabels()}
                    <BodyText>
                        Max Financed{' '}
                        <strong>{formatCurrency(maxFinancedAmount)}</strong>
                        <br />
                        <span>{getAdvanceText()}</span>
                        <AprText>
                            Rates as low as <strong>{minAPR}%</strong>
                        </AprText>
                    </BodyText>
                </>
            )}

            <a
                href={`#/application/status/${clientApplication?.id}`}
                style={{ width: '100%' }}
            >
                <Button fullWidth id="workTheDeal-button">
                    Work the Deal
                </Button>
            </a>
        </Container>
    )
}
